import React from 'react';
import {
  Card,
  Typography,
  Button,
  Image,
  Flex,
  Row,
  Col,
  Tag,
  Space,
  Collapse,
  CollapseProps,
} from 'antd';
import {
  DownloadOutlined,
  SaveOutlined,
  ReloadOutlined,
  ExperimentOutlined,
  CheckOutlined,
} from '@ant-design/icons';
import { IGenerationTask, GenerationTaskProps } from '../../types/generationTask';
import { Loader } from '../UI/Loader';

const { Text, Title } = Typography;

const styles = {
  collapse: {
    width: '100%',
    backgroundColor: '#ffffff',
    border: '1px solid #e8e8e8',
    borderRadius: '8px',
    overflow: 'hidden',
    marginBottom: '16px',
    boxShadow: '0 1px 2px rgba(0,0,0,0.03)',
  } as React.CSSProperties,
  card: {
    width: '100%',
    backgroundColor: '#ffffff',
    border: 'none',
  } as React.CSSProperties,
  cardBody: {
    padding: 0,
    overflow: 'hidden',
  } as React.CSSProperties,
  imageContainer: {
    position: 'relative' as const,
    height: '250px',
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    overflow: 'hidden',
    borderRadius: '8px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
    margin: '0 auto',
  } as React.CSSProperties,
  image: {
    width: '100%',
    height: '100%',
    objectFit: 'cover' as const,
  } as React.CSSProperties,
  loaderOverlay: {
    position: 'absolute' as const,
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(0, 0, 0, 0.2)',
    backdropFilter: 'blur(2px)',
    borderRadius: '8px',
  } as React.CSSProperties,
  detailsContainer: {
    height: '100%',
    padding: '16px',
  } as React.CSSProperties,
  actionButtons: {
    marginTop: '16px',
  } as React.CSSProperties,
  titleStyle: {
    marginTop: '8px',
  } as React.CSSProperties,
};

interface TaskStatusProps {
  status: 'created' | 'pending' | 'done' | 'failure';
  createdAt: string;
}

interface TaskImageProps {
  task: IGenerationTask;
  imageUrl: string;
}

interface TaskDetailsProps {
  task: IGenerationTask;
}

interface ActionButtonsProps {
  task: IGenerationTask;
  onRetry: (e: React.MouseEvent<HTMLElement>) => void;
  onDownload: (e: React.MouseEvent<HTMLElement>) => void;
  onSave: (e: React.MouseEvent<HTMLElement>) => void;
  onUseIt?: (e: React.MouseEvent<HTMLElement>) => void;
  disabled?: boolean;
  upscaleImage?: (e: React.MouseEvent<HTMLElement>) => void;
}

const getStatusColor = (status: 'created' | 'pending' | 'done' | 'failure'): string => {
  switch (status) {
    case 'created':
      return 'blue';
    case 'pending':
      return 'orange';
    case 'done':
      return 'green';
    case 'failure':
      return 'red';
    default:
      return 'default';
  }
};

const getImageUrl = (task: IGenerationTask): string => {
  if (task.status !== 'done' || !task.output?.result) {
    return (
      task.input.image ||
      task.input.control_image ||
      task.input.image_url ||
      '/public/images/bird.png'
    );
  }
  return task.output.result;
};

const TaskStatus: React.FC<TaskStatusProps> = ({ status, createdAt }) => (
  <Space>
    <Tag color={getStatusColor(status)}>{status.toUpperCase()}</Tag>
    <Text type="secondary">{new Date(createdAt).toLocaleString()}</Text>
  </Space>
);

const TaskImage: React.FC<TaskImageProps> = ({ task, imageUrl }) => (
  <div style={styles.imageContainer}>
    <Image
      src={task.output?.result || imageUrl}
      alt={task.id}
      style={styles.image}
      preview={task.status === 'done' && !!task.output?.result}
    />
    {task.status !== 'done' && task.status !== 'failure' && (
      <div style={styles.loaderOverlay}>
        <Loader />
      </div>
    )}
  </div>
);

const TaskDetails: React.FC<TaskDetailsProps> = ({ task }) => (
  <Space direction="vertical" size="small" style={{ width: '100%' }}>
    <TaskStatus status={task.status} createdAt={task.createdAt} />

    <Title level={4} style={styles.titleStyle}>
      {task.input.model} - {task.input.style || 'Custom Style'}
    </Title>

    <Text>{task.input.prompt || 'No prompt provided'}</Text>
  </Space>
);

const ActionButtons: React.FC<ActionButtonsProps> = ({
  task,
  onRetry,
  onDownload,
  onSave,
  onUseIt,
  disabled,
  upscaleImage,
}) => (
  <Space style={styles.actionButtons}>
    {task.status === 'done' && task.output?.result && (
      <>
        <Button icon={<DownloadOutlined />} onClick={onDownload} disabled={disabled}>
          Download
        </Button>
        <Button type="primary" icon={<SaveOutlined />} onClick={onSave} disabled={disabled}>
          Save to Plan
        </Button>
        {onUseIt && (
          <Button type="primary" icon={<CheckOutlined />} onClick={onUseIt} disabled={disabled}>
            Use it
          </Button>
        )}
        <Button
          type="primary"
          icon={<ExperimentOutlined />}
          onClick={upscaleImage}
          disabled={disabled}>
          Upscale
        </Button>
      </>
    )}
    {(task.status === 'done' || task.status === 'created' || task.status === 'failure') && (
      <Button icon={<ReloadOutlined />} onClick={onRetry} disabled={disabled}>
        Retry
      </Button>
    )}
  </Space>
);

const GenerationTaskItem: React.FC<GenerationTaskProps> = ({
  task,
  onRetry,
  onDownload,
  onSave,
  onUseIt,
  disabled,
  upscaleImage,
}) => {
  const handleRetry = (e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (onRetry) {
      onRetry(task.id);
    }
  };

  const handleDownload = (e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (onDownload && task.output?.result) {
      onDownload(task.output.result);
    }
  };

  const handleSave = (e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (onSave && task.output?.result) {
      onSave(task.id);
    }
  };

  const handleUseIt = (e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (onUseIt && task.output?.result) {
      onUseIt(task.id);
    }
  };

  const handleUpscale = (e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (upscaleImage) {
      upscaleImage(task);
    }
  };

  const imageUrl = getImageUrl(task);
  const collapseItems: CollapseProps['items'] = [
    {
      key: task.id,
      label: (
        <TaskLabel
          task={task}
          imageUrl={imageUrl}
          onRetry={handleRetry}
          onDownload={handleDownload}
          onSave={handleSave}
          onUseIt={handleUseIt}
          upscaleImage={handleUpscale}
          disabled={disabled}
        />
      ),
      children: <>{task.id}</>,
    },
  ];

  return (
    <Collapse
      bordered={false}
      defaultActiveKey={['1']}
      style={styles.collapse}
      items={collapseItems}
    />
  );
};

interface TaskLabelProps {
  task: IGenerationTask;
  imageUrl: string;
  onRetry: (e: React.MouseEvent<HTMLElement>) => void;
  onDownload: (e: React.MouseEvent<HTMLElement>) => void;
  onSave: (e: React.MouseEvent<HTMLElement>) => void;
  onUseIt?: (e: React.MouseEvent<HTMLElement>) => void;
  disabled?: boolean;
  upscaleImage?: (e: React.MouseEvent<HTMLElement>) => void;
}

const TaskLabel: React.FC<TaskLabelProps> = ({
  task,
  imageUrl,
  onRetry,
  onDownload,
  onSave,
  onUseIt,
  disabled,
  upscaleImage,
}) => (
  <Card
    style={styles.card}
    styles={{
      body: styles.cardBody,
    }}>
    <Row gutter={[16, 16]}>
      <Col span={24} lg={8}>
        <TaskImage task={task} imageUrl={imageUrl} />
      </Col>
      <Col span={24} lg={16}>
        <Flex vertical align="flex-start" justify="space-between" style={styles.detailsContainer}>
          <div>
            <TaskDetails task={task} />
          </div>
          <ActionButtons
            task={task}
            onRetry={onRetry}
            onDownload={onDownload}
            onSave={onSave}
            onUseIt={onUseIt}
            upscaleImage={upscaleImage}
            disabled={disabled}
          />
        </Flex>
      </Col>
    </Row>
  </Card>
);

export default GenerationTaskItem;
